import { VAlign, VAlignCommand } from './types'

const valignCommandConfig: Map<
  VAlign,
  {
    valign: VAlign
    command: VAlignCommand
    button: string
    label: string
  }
> = new Map([
  [
    VAlign.MIDDLE,
    {
      valign: VAlign.MIDDLE,
      command: VAlignCommand.MIDDLE,
      button: 'VerticalAlignMiddle',
      label: 'По середине',
    },
  ],
  [
    VAlign.BOTTOM,
    {
      valign: VAlign.BOTTOM,
      command: VAlignCommand.BOTTOM,
      button: 'VerticalAlignBottom',
      label: 'По нижнему краю',
    },
  ],
  [
    VAlign.TOP,
    {
      valign: VAlign.TOP,
      command: VAlignCommand.TOP,
      button: 'VerticalAlignTop',
      label: 'По верхнему краю',
    },
  ],
])

const WRAPPER_CLASSNAME = 'va-wrapper'

const getVAlign = (editor: CKEDITOR.editor) => {
  const vAlign = editor
    .editable()
    .findOne(`.${WRAPPER_CLASSNAME}`)
    ?.getAttribute('data-valign') as VAlign

  return vAlign || VAlign.TOP
}

function refreshCommandsState(editor: CKEDITOR.editor, currentVAlign: VAlign) {
  for (const { command: commandName, valign } of valignCommandConfig.values()) {
    const command = editor.getCommand(commandName)

    if (command) {
      command.setState(
        currentVAlign === valign ? CKEDITOR.TRISTATE_ON : CKEDITOR.TRISTATE_OFF
      )
    }
  }
}

const moveChildren = (
  sourceEl: CKEDITOR.dom.element,
  targetEl: CKEDITOR.dom.element,
  editorDocument: CKEDITOR.dom.document,
  excludeEl?: CKEDITOR.dom.element | null
) => {
  const children = sourceEl.getChildren().toArray().slice()

  children.forEach((childEl) => {
    if (
      excludeEl &&
      childEl.type === CKEDITOR.NODE_ELEMENT &&
      childEl.equals(excludeEl)
    ) {
      return
    }

    childEl.remove()

    if (childEl.type === CKEDITOR.NODE_TEXT) {
      if ((childEl as CKEDITOR.dom.text).getText().trim()) {
        const p = editorDocument.createElement('p')
        p.append(childEl)
        targetEl.append(p)
      }
    } else {
      targetEl.append(childEl)
    }
  })
}

const sanitizeTextNodes = (
  containerEl: CKEDITOR.dom.element,
  editorDocument: CKEDITOR.dom.document
) => {
  const children = containerEl.getChildren().toArray().slice()

  children.forEach((childEl) => {
    if (childEl.type === CKEDITOR.NODE_TEXT) {
      const text = (childEl as CKEDITOR.dom.text).getText()

      if (text.trim()) {
        const p = editorDocument.createElement('p')
        childEl.remove()
        p.append(childEl)
        containerEl.append(p)
      } else {
        childEl.remove()
      }
    }
  })
}

const applyVAlign = (value: VAlign, editor: CKEDITOR.editor) => {
  editor.focus()

  const currentVAlign = getVAlign(editor)

  if (currentVAlign === value) {
    return
  }

  const editorEl = editor.editable()

  let wrapperEl = editorEl.findOne(
    `.${WRAPPER_CLASSNAME}`
  ) as CKEDITOR.dom.element | null

  if (value === VAlign.TOP) {
    if (wrapperEl) {
      sanitizeTextNodes(wrapperEl, editor.document)

      moveChildren(wrapperEl, editorEl, editor.document)
      wrapperEl.remove()
      wrapperEl = null
    }
  } else if (!wrapperEl) {
    wrapperEl = editor.document.createElement('div')
    wrapperEl.addClass(WRAPPER_CLASSNAME)

    editorEl.append(wrapperEl)
    sanitizeTextNodes(editorEl, editor.document)
    moveChildren(editorEl, wrapperEl, editor.document, wrapperEl)
  }

  wrapperEl?.setAttribute('data-valign', value)

  const range = editor.createRange()
  range.moveToElementEditEnd(range.root)
  editor.getSelection().selectRanges([range])

  refreshCommandsState(editor, value)

  editor.focus()
  editor.fire('change')
}

export const verticalAlignPlugin: CKEDITOR.pluginDefinition = {
  init(editor) {
    editor.addFeature({
      allowedContent: `div(${WRAPPER_CLASSNAME})[data-valign]`,
    })

    for (const {
      button,
      label,
      command,
      valign,
    } of valignCommandConfig.values()) {
      editor.addCommand(command, {
        exec: () => {
          applyVAlign(valign, editor)

          return false
        },
      })

      editor.ui.addButton(button, {
        label,
        command,
        toolbar: command,
      })
    }

    editor.on('selectionChange', () => {
      refreshCommandsState(editor, getVAlign(editor))
    })

    editor.on('change', () => {
      const editorEl = editor.editable()
      const wrapper = editorEl.findOne(
        `.${WRAPPER_CLASSNAME}`
      ) as CKEDITOR.dom.element | null

      if (wrapper) {
        sanitizeTextNodes(wrapper, editor.document)
      } else {
        sanitizeTextNodes(editorEl, editor.document)
      }
    })
  },
}
